import { Table, ApiClientConstant } from '@cureskin/api-client';
import { Component, Input, Output, EventEmitter, input, inject, OnInit, OnDestroy, ViewChild, ElementRef, ViewEncapsulation } from '@angular/core';
import { DOCUMENT, CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { OrderService } from '@services/order-service/order.service';
import { ConnectionService } from '@services/connection-service';
import { EventLoggerService } from '@services/event-logger-service';
import { InstructionsService, BannerInstructionVideo } from '@services/instructions-service/instructions.service';
import { CsIconModule } from '@components/icon';
import { SwiperModule } from 'swiper/angular';
import { AllocatedRegimen } from '../../../app-constant-types';
import { ORDER_DELIVERY_BANNER, KIT_IMAGE_FALLBACKS } from '../../../app.constants';

@Component({
  selector: 'order-delivered',
  imports: [CsIconModule, CommonModule, RouterModule, SwiperModule],
  templateUrl: './order-delivered.component.html',
  encapsulation: ViewEncapsulation.None,
  host: {
    '(document:fullscreenchange)': 'onFullscreenChange()',
  },
  styles: [`
    order-delivered .swiper-slide {
      width: auto !important;
    }
  `],
})
export class OrderDeliveredComponent implements OnInit, OnDestroy {
  public readonly orderService: OrderService = inject(OrderService);
  private readonly conn: ConnectionService = inject(ConnectionService);
  private readonly instructionsService: InstructionsService = inject(InstructionsService);
  private readonly router: Router = inject(Router);
  private readonly eventLogger: EventLoggerService = inject(EventLoggerService);
  private readonly document: Document = inject(DOCUMENT);
  public readonly ApiClientConstant = ApiClientConstant;

  orderDeliveryBannerVideosExp = input<boolean>(false);
  // True only when the regimen-delivered carousel has more than one slide, i.e.
  // pagination dots are shown and the banner needs bottom spacing for them.
  partialVideos = input<boolean>(false);
  kitRegimen = input<AllocatedRegimen>();
  @Input() order: Table.Order;
  @Output() addEvents: EventEmitter<any> = new EventEmitter();
  @ViewChild('bannerVideoPlayer') bannerVideoPlayer: ElementRef<HTMLVideoElement>;

  orderedProductsCount: number;
  cancelledProducts: Array<Table.Order['attributes']['productApprovalDetails']['productDetails'][number]> = [];
  shouldShowPartialOrderCancellationUI: boolean;
  cancelledProductsCount: number = 0;
  totalProductsCount: number = 0;

  productImages: string[] = [];
  currentImageIndex: number = 0;
  private imageRotationInterval: ReturnType<typeof setInterval>;

  instructionVideos: BannerInstructionVideo[] = [];
  totalRegimenProducts: number = 0;
  titlePrefix: string = '';
  isVideoPlaying: boolean = false;
  readonly videoCardBackgrounds: string[] = ['#E5F1EE', '#F7F3D0', '#EDE8F1', '#EBF1CE'];
  readonly swiperConfig = {
    slidesPerView: 'auto' as const,
    spaceBetween: 8,
    slidesOffsetBefore: 16,
    slidesOffsetAfter: 16,
    nested: true,
    observer: true,
    observeParents: true,
    navigation: { prevEl: 'nonext', nextEl: 'nonext' },
  };

  constructor() {}

  get orderUrl(): string {
    return `/user/order/${this.order?.id}`;
  }

  get kitClass(): string {
    return this.kitRegimen()?.class;
  }

  get kitImage(): string {
    return this.kitRegimen()?.regimenCoverImage
      || (this.kitClass === ApiClientConstant.Regimen.Class.HAIR ? KIT_IMAGE_FALLBACKS.HAIR : KIT_IMAGE_FALLBACKS.FACE);
  }

  get hasAllVideos(): boolean {
    return this.totalRegimenProducts > 0 && this.instructionVideos.length >= this.totalRegimenProducts;
  }

  get showVideoCarousel(): boolean {
    if (!this.orderDeliveryBannerVideosExp()) return false;
    if (this.hasAllVideos) return true;
    return this.instructionVideos.length >= ORDER_DELIVERY_BANNER.MIN_PARTIAL_VIDEOS && this.partialVideos();
  }

  get showSeeMoreInstructionsTile(): boolean {
    return this.orderDeliveryBannerVideosExp() && !this.hasAllVideos
      && this.instructionVideos.length >= ORDER_DELIVERY_BANNER.MIN_PARTIAL_VIDEOS && this.partialVideos();
  }

  ngOnInit(): void {
    const approvalData = this.orderService.initializeProductApprovalData(this.order);
    this.cancelledProducts = approvalData.cancelledProducts;
    this.orderedProductsCount = approvalData.orderedProductsCount;
    this.shouldShowPartialOrderCancellationUI = approvalData.shouldShowPartialOrderCancellationUI;
    this.cancelledProductsCount = approvalData.cancelledProductsCount;
    this.totalProductsCount = approvalData.totalProductsCount;

    this.initImageRotation();
    if (this.orderDeliveryBannerVideosExp()) {
      const user = this.conn.getActingUser();
      const firstName = user?.get('PatientName')?.split(' ')[0];
      this.titlePrefix = firstName ? `${firstName}, ` : '';
      const { videos, totalProducts } = this.instructionsService
        .extractRegimenInstructionVideos(this.kitRegimen(), user, ORDER_DELIVERY_BANNER.MAX_VIDEOS);
      this.instructionVideos = videos;
      this.totalRegimenProducts = totalProducts;
      this.loadVideoDurations();
      this.eventLogger.cleverTapEvent('impression', JSON.stringify({
        banner_name: 'order_delivery_banner_new',
        no_of_videos: this.instructionVideos.length,
      }));
    }
  }

  ngOnDestroy(): void {
    if (this.imageRotationInterval) {
      clearInterval(this.imageRotationInterval);
    }
    this.closeVideo();
    this.bannerVideoPlayer?.nativeElement?.parentElement?.remove();
  }

  onFullscreenChange(): void {
    if (!this.document.fullscreenElement) {
      this.closeVideo();
    }
  }

  handleClick(): void {
    if (this.orderDeliveryBannerVideosExp()) {
      this.eventLogger.cleverTapEvent('click', JSON.stringify({ name: 'order_delivery_banner_new' }));
    }
    this.addEvents.emit();
  }

  playVideo(video: BannerInstructionVideo): void {
    this.eventLogger.cleverTapEvent('click', JSON.stringify({ name: 'order_delivery_banner_video_played' }));
    const videoPlayer = this.bannerVideoPlayer?.nativeElement;
    if (!videoPlayer) return;
    const overlay = videoPlayer.parentElement;
    if (overlay.parentElement !== this.document.body) {
      this.document.body.appendChild(overlay);
    }
    overlay.classList.remove('tw-hidden');
    videoPlayer.src = video.url;
    const playPromise = videoPlayer.play();
    if (playPromise && !this.isVideoPlaying) {
      playPromise.then((): void => {
        this.isVideoPlaying = true;
      });
    }
  }

  closeVideo(): void {
    if (!this.isVideoPlaying) return;
    const videoPlayer = this.bannerVideoPlayer?.nativeElement;
    if (!videoPlayer) return;
    videoPlayer.pause();
    videoPlayer.currentTime = 0;
    videoPlayer.parentElement.classList.add('tw-hidden');
    this.isVideoPlaying = false;
  }

  navigateToInstructions(): void {
    this.eventLogger.cleverTapEvent('click', JSON.stringify({ name: 'order_delivery_banner_new' }));
    this.addEvents.emit();
    const kitRegimen = this.kitRegimen();
    if (kitRegimen) {
      this.router.navigate(['/regimen/instructions'], {
        queryParams: { class: kitRegimen.class, regimenId: kitRegimen.regimenId },
      });
      return;
    }
    this.conn.navigateToURL('/user?tab=regimen');
  }

  private loadVideoDurations(): void {
    this.instructionVideos.forEach((video: BannerInstructionVideo): void => {
      const probe = this.document.createElement('video');
      probe.preload = 'metadata';
      probe.onloadedmetadata = (): void => {
        if (Number.isFinite(probe.duration)) {
          const totalSeconds = Math.round(probe.duration);
          const minutes = Math.floor(totalSeconds / 60);
          const seconds = `${totalSeconds % 60}`.padStart(2, '0');
          video.durationLabel = `${minutes}:${seconds}`;
        }
        probe.removeAttribute('src');
      };
      probe.src = video.url;
    });
  }

  private initImageRotation(): void {
    if (!this.order) {
      return;
    }
    this.extractProductImages();
    if (this.productImages.length > 1) {
      this.imageRotationInterval = setInterval(() => {
        this.currentImageIndex = (this.currentImageIndex + 1) % this.productImages.length;
      }, 2000);
    }
  }

  private extractProductImages(): void {
    if (this.order.get('type') === ApiClientConstant.Order.Type.REGIMEN) {
      const regimenCoverImage = this.order.get('regimen')?.get('regimenCoverImage');
      if (regimenCoverImage) {
        this.productImages = [regimenCoverImage];
      } else {
        const regimenClass = this.order.get('regimen')?.get('class');
        const fallback = regimenClass === 'FACE'
          ? 'https://cdn.cureskin.com/app/img/Skin-Box.png'
          : 'https://cdn.cureskin.com/app/img/Hair-Box.png';
        this.productImages = [fallback];
      }
    } else {
      const rawProducts: any[] = this.order.get('products') || [];
      const products: any[] = JSON.parse(JSON.stringify(rawProducts))
        .map((each: any) => each.product || each);
      this.productImages = products
        .map((product: any) => {
          return (product.rebrandedImageWithoutBackground?.length && product.rebrandedImageWithoutBackground[0])
            || product.productUnboxedImage?.[0]
            || (product.rebrandedImageWithBackground?.length && product.rebrandedImageWithBackground[0]);
        })
        .filter((img: string) => !!img);
    }
  }
}
